import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { ExternalLink, UserRound, Disc, ChevronRight, Edit, Trash2, Plus, BarChart2 } from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { ResearchAssistantPanel } from "@/components/ResearchAssistantPanel"
import { ImageGallery, type GalleryImage } from "@/components/ImageGallery"
import { PhotosTab } from "@/components/PhotosTab"
import { RequestDeletionButton } from "@/components/RequestDeletionButton"
import { videoEmbedUrl } from "@/lib/videoEmbed"
import { SimpleFormat } from "@/components/SimpleFormat"

type Image = { url: string; og_image_url: string; alt_text: string }

type Credit = {
  id: number
  role: string
  details: string | null
  person_id: number
  person_name: string
  person_slug: string
  person_image: Image | null
}

type Video = { id: number; name: string; url: string }
type RecordingLink = { id: number; link_text: string; url: string }
type ChartListing = { id: number; chart_date: string | null; position: number }

type RecordingDetail = {
  id: number
  title: string
  slug: string
  from_album: string | null
  completeness_score: number
  recordable_type: string
  recordable_id: number
  recordable_name: string | null
  recordable_slug: string | null
  recording_type: string | null
  catalogue_number: string | null
  comments: string | null
  released_year: number | null
  released_month: number | null
  first_charted: string | null
  highest_position: number | null
  weeks_in_chart: number | null
  record_label: string | null
  record_label_slug: string | null
  purchase_link_url: string | null
  purchase_link_text: string | null
  meta_title: string | null
  meta_description: string | null
  primary_image: Image | null
  images: GalleryImage[]
  credits: Credit[]
  videos: Video[]
  links: RecordingLink[]
  chart_listings: ChartListing[]
}

type Permissions = { can_edit: boolean; can_delete: boolean; can_request_deletion: boolean }
type Tab = "chart" | "credits" | "videos" | "links" | "photos"

const MONTHS = [ "January", "February", "March", "April", "May", "June",
                 "July", "August", "September", "October", "November", "December" ]

const SHORT_MONTHS = [ "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ]

function formatChartDate(iso: string | null): string {
  if (!iso) return "—"
  const [, m, d] = iso.split("-").map(Number)
  return `${d} ${SHORT_MONTHS[m - 1]}`
}

function formatChartYear(iso: string | null): string {
  if (!iso) return ""
  return iso.slice(0, 4)
}


function ChartListingsTab({ listings, canEdit, recordingSlug }: { listings: ChartListing[]; canEdit: boolean; recordingSlug: string }) {
  const maxPosition = Math.max(...listings.map((cl) => cl.position), 1)
  let lastYear = ""

  return (
    <div className="max-w-sm">
      <div className="divide-y divide-stone-100 dark:divide-stone-800/60">
        {listings.map((cl) => {
          const year = formatChartYear(cl.chart_date)
          const showYear = year !== lastYear
          lastYear = year
          const isNumber1 = cl.position === 1
          const barWidth = Math.round(((maxPosition - cl.position + 1) / maxPosition) * 100)

          return (
            <div key={cl.id} className="flex items-center gap-3 py-2.5 group/cl">
              {/* Year — only shown when it changes */}
              <span
                className="w-10 shrink-0 text-xs text-stone-400 dark:text-stone-500 tabular-nums text-right"
                style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
              >
                {showYear ? year : ""}
              </span>

              {/* Date within year */}
              <span
                className="w-14 shrink-0 text-xs text-stone-500 dark:text-stone-400 tabular-nums"
                style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
              >
                {formatChartDate(cl.chart_date)}
              </span>

              {/* Bar */}
              <div className="flex-1 h-1.5 bg-stone-100 dark:bg-stone-800 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${isNumber1 ? "bg-amber-400" : "bg-stone-400 dark:bg-stone-500"}`}
                  style={{ width: `${barWidth}%` }}
                />
              </div>

              {/* Position */}
              <span
                className={`w-8 shrink-0 text-right text-sm font-semibold tabular-nums ${isNumber1 ? "text-amber-500" : "text-stone-700 dark:text-stone-300"}`}
                style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
              >
                #{cl.position}
              </span>

              {/* Admin controls */}
              {canEdit && (
                <div className="flex items-center gap-1 opacity-0 group-hover/cl:opacity-100 transition-opacity">
                  <Link
                    href={`/chart_listings/${cl.id}/edit?return_to=/recordings/${recordingSlug}`}
                    className="p-1 rounded text-stone-400 hover:text-amber-600 no-underline"
                    title="Edit"
                  >
                    <Edit className="w-3 h-3" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm("Delete this chart listing?"))
                        router.delete(`/chart_listings/${cl.id}`, { data: { return_to: `/recordings/${recordingSlug}` } })
                    }}
                    className="p-1 rounded text-stone-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {canEdit && (
        <div className="mt-4">
          <Link
            href={`/recordings/${recordingSlug}/chart_listings/new`}
            className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 no-underline"
          >
            <Plus className="w-3.5 h-3.5" />
            Add chart listing
          </Link>
        </div>
      )}
    </div>
  )
}

export default function RecordingShow({ recording, permissions }: { recording: RecordingDetail; permissions: Permissions }) {

  const tabCounts: Record<Tab, number> = {
    chart:   recording.chart_listings.length,
    credits: recording.credits.length,
    videos:  recording.videos.length,
    links:   recording.links.length,
    photos:  recording.images.length,
  }
  const visibleTabs = (["chart", "credits", "videos", "links", "photos"] as Tab[]).filter(
    (t) => tabCounts[t] > 0 || permissions.can_edit
  )
  const [activeTab, setActiveTab] = useState<Tab | null>(() => visibleTabs[0] ?? null)

  const TAB_LABELS: Record<Tab, string> = {
    chart:   "Chart run",
    credits: "Credits",
    videos:  "Videos",
    links:   "Links",
    photos:  "Photos",
  }

  const recordablePath = recording.recordable_slug
    ? `/${recording.recordable_type === "Band" ? "bands" : "people"}/${recording.recordable_slug}`
    : null

  const description = recording.meta_description
    ?? recording.comments?.replace(/<[^>]+>/g, "").slice(0, 160)
    ?? `${recording.title}${recording.recordable_name ? ` by ${recording.recordable_name}` : ""} — Australian Music Database.`

  const ogTitle = recording.meta_title ?? recording.title

  return (
    <>
      <Head title={recording.title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="music.song" />
        {recording.primary_image?.og_image_url && <meta property="og:image" content={recording.primary_image.og_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
      </Head>

      <PublicShell>
        {/* Hero */}
        <div className="flex flex-col sm:flex-row sm:items-start gap-6 mb-8">
          <ImageGallery
            images={recording.images ?? []}
            emptyIcon={<Disc className="w-14 h-14 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />}
          />

          <div className="flex-1 min-w-0">
            <h1
              className="text-2xl sm:text-3xl font-bold text-stone-900 dark:text-white leading-tight tracking-tight mb-2"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
            >
              {recording.title}
            </h1>

            {recording.recordable_name && recordablePath && (
              <p className="text-base text-stone-600 dark:text-stone-400 mb-3">
                by{" "}
                <Link
                  href={recordablePath}
                  className="font-medium text-stone-900 dark:text-white hover:text-amber-600 dark:hover:text-amber-400 transition-colors no-underline"
                  style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                >
                  {recording.recordable_name}
                </Link>
              </p>
            )}

            {/* Meta strip */}
            <div
              className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-stone-500 dark:text-stone-400 mb-4"
              style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
            >
              {recording.recording_type && (
                <span className="px-1.5 py-0.5 rounded bg-stone-100 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-300">
                  {recording.recording_type}
                </span>
              )}
              {(recording.released_year || recording.released_month) && (
                <span>
                  {recording.released_month ? MONTHS[recording.released_month - 1] + " " : ""}
                  {recording.released_year}
                </span>
              )}
              {recording.record_label && (
                recording.record_label_slug ? (
                  <Link href={`/record_labels/${recording.record_label_slug}`} className="hover:text-amber-600 dark:hover:text-amber-400 transition-colors">
                    {recording.record_label}
                  </Link>
                ) : (
                  <span>{recording.record_label}</span>
                )
              )}
              {recording.catalogue_number && <span>{recording.catalogue_number}</span>}
              {recording.from_album && <span>from the album <em>{recording.from_album}</em></span>}
            </div>

            {/* Chart summary */}
            {recording.highest_position !== null && (
              <div
                className="flex items-center gap-3 mb-4 text-sm"
                style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}
              >
                <span className={`text-lg font-bold tabular-nums ${recording.highest_position === 1 ? "text-amber-500" : "text-stone-700 dark:text-stone-300"}`}>
                  #{recording.highest_position}
                </span>
                <span className="text-stone-400 dark:text-stone-500">
                  highest chart position
                  {recording.weeks_in_chart ? ` · ${recording.weeks_in_chart} weeks` : ""}
                </span>
              </div>
            )}

            {/* Comments / description */}
            {recording.comments && (
              <div className="text-sm text-stone-600 dark:text-stone-400 leading-relaxed mb-4 [&_p]:mb-2 [&_p:last-child]:mb-0">
                <SimpleFormat text={recording.comments} />
              </div>
            )}

            {/* Purchase link */}
            {recording.purchase_link_url && (
              <a
                href={recording.purchase_link_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-amber-600 dark:text-amber-400 hover:underline no-underline"
              >
                {recording.purchase_link_text || "Buy / Stream"}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}

            {permissions.can_edit && (
              <div className="mt-3 mb-1">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400">
                  <BarChart2 className="w-3 h-3" />
                  Score: {recording.completeness_score}/9
                </span>
              </div>
            )}

            {(permissions.can_edit || permissions.can_delete || permissions.can_request_deletion) && (
              <div className="mt-4 flex items-center gap-2">
                {permissions.can_edit && (
                  <Link
                    href={`/recordings/${recording.slug}/edit`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-stone-700 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 rounded-lg transition-colors no-underline"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Edit
                  </Link>
                )}
                {permissions.can_delete && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Delete "${recording.title}"? This cannot be undone.`)) {
                        router.delete(`/recordings/${recording.slug}`, {
                          data: { return_to: recording.recordable_slug
                            ? `/${recording.recordable_type === "Band" ? "bands" : "people"}/${recording.recordable_slug}`
                            : "/recordings"
                          }
                        })
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 bg-stone-100 dark:bg-stone-800 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete
                  </button>
                )}
                {permissions.can_request_deletion && (
                  <RequestDeletionButton type="Recording" id={recording.id} name={recording.title} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        {visibleTabs.length > 0 && (
          <>
            <div className="border-b border-stone-200 dark:border-stone-800 mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8">
              <div className="flex gap-0 -mb-px overflow-x-auto">
                {visibleTabs.map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={[
                      "px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors focus-visible:outline-none",
                      activeTab === tab
                        ? "border-amber-500 text-amber-600 dark:text-amber-400"
                        : "border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-white hover:border-stone-300 dark:hover:border-stone-600",
                    ].join(" ")}
                  >
                    {TAB_LABELS[tab]}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart run */}
            {activeTab === "chart" && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <BarChart2 className="w-4 h-4 text-stone-400" />
                  <span className="text-sm text-stone-500 dark:text-stone-400" style={{ fontFamily: "'IBM Plex Mono', ui-monospace, monospace" }}>
                    {recording.chart_listings.length} week{recording.chart_listings.length !== 1 ? "s" : ""} on chart
                  </span>
                </div>
                <ChartListingsTab listings={recording.chart_listings} canEdit={permissions.can_edit} recordingSlug={recording.slug} />
              </div>
            )}

            {/* Credits */}
            {activeTab === "credits" && (
              <div>
                {permissions.can_edit && (
                  <div className="mb-4">
                    <Link
                      href={`/credits/new?creditable_type=Recording&creditable_id=${recording.id}&return_to=${encodeURIComponent(`/recordings/${recording.slug}`)}`}
                      className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add credit
                    </Link>
                  </div>
                )}
                {recording.credits.length === 0 ? (
                  <p className="text-sm text-stone-400 dark:text-stone-500">No credits added yet.</p>
                ) : (
                  <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))" }}>
                    {recording.credits.map((credit) => (
                      <Link
                        key={credit.id}
                        href={`/people/${credit.person_slug}`}
                        className="group block text-left bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 rounded-xl overflow-hidden hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-sm transition-all duration-150 no-underline"
                      >
                        <div className="aspect-square bg-stone-100 dark:bg-stone-800 overflow-hidden">
                          {credit.person_image ? (
                            <img src={credit.person_image.url} alt={credit.person_image.alt_text} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <UserRound className="w-16 h-16 text-stone-300 dark:text-stone-600" strokeWidth={0.75} />
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <div className="text-sm font-semibold text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug truncate" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                            {credit.person_name}
                          </div>
                          <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded-full bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 leading-none mt-1.5">
                            {credit.role}
                          </span>
                          {credit.details && (
                            <p className="text-xs text-stone-400 dark:text-stone-500 mt-1 truncate">{credit.details}</p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Videos */}
            {activeTab === "videos" && (
              <div>
                {permissions.can_edit && (
                  <div className="mb-4">
                    <Link
                      href={`/videos/new?pre_attach_type=Recording&pre_attach_id=${recording.id}&return_to=${encodeURIComponent(`/recordings/${recording.slug}`)}`}
                      className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add video
                    </Link>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {recording.videos.map((video) => (
                    <div key={video.id} className="rounded-xl overflow-hidden border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900">
                      <div className="aspect-video w-full">
                        <iframe
                          src={videoEmbedUrl(video.url)}
                          title={video.name}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                      <div className="px-3.5 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-medium text-stone-700 dark:text-stone-300 truncate flex-1" style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}>
                            {video.name}
                          </span>
                          {permissions.can_edit && (
                            <div className="flex items-center gap-2 shrink-0">
                              <Link
                                href={`/videos/${video.id}/edit?return_to=${encodeURIComponent(`/recordings/${recording.slug}`)}`}
                                className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-amber-600 dark:hover:text-amber-400 no-underline transition-colors"
                              >
                                <Edit className="w-3 h-3" /> Edit
                              </Link>
                              <button
                                type="button"
                                onClick={() => {
                                  if (confirm("Delete this video?")) {
                                    router.delete(`/videos/${video.id}`, { data: { return_to: `/recordings/${recording.slug}` } })
                                  }
                                }}
                                className="inline-flex items-center gap-1 text-xs text-stone-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
                              >
                                <Trash2 className="w-3 h-3" /> Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Links */}
            {activeTab === "links" && (
              <div>
                <div className="divide-y divide-stone-100 dark:divide-stone-800/80">
                  {recording.links.map((link) => (
                    <div key={link.id} className="flex items-center gap-3.5 py-3.5 group/lnk">
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3.5 flex-1 min-w-0 no-underline group"
                      >
                        <div className="w-8 h-8 rounded-lg bg-stone-100 dark:bg-stone-800 flex items-center justify-center shrink-0 group-hover:bg-amber-50 dark:group-hover:bg-amber-950/40 border border-stone-200 dark:border-stone-700 group-hover:border-amber-200 dark:group-hover:border-amber-800 transition-colors">
                          <ExternalLink className="w-3.5 h-3.5 text-stone-400 group-hover:text-amber-500 transition-colors" />
                        </div>
                        <span className="text-sm font-medium text-stone-900 dark:text-white group-hover:text-amber-600 dark:group-hover:text-amber-400 transition-colors leading-snug flex-1">
                          {link.link_text}
                        </span>
                        <ChevronRight className="w-4 h-4 text-stone-300 dark:text-stone-600 shrink-0" />
                      </a>
                      {permissions.can_edit && (
                        <div className="flex items-center gap-1 opacity-0 group-hover/lnk:opacity-100 transition-opacity shrink-0">
                          <Link href={`/links/${link.id}/edit?return_to=/recordings/${recording.slug}`} className="p-1 rounded text-stone-400 hover:text-amber-600 no-underline" title="Edit"><Edit className="w-3.5 h-3.5" /></Link>
                          <button type="button" onClick={() => { if (confirm("Delete this link?")) router.delete(`/links/${link.id}`, { data: { return_to: `/recordings/${recording.slug}` } }) }} className="p-1 rounded text-stone-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {permissions.can_edit && (
                  <div className="mt-4">
                    <Link href={`/links/new?linkable_type=Recording&linkable_id=${recording.id}&return_to=/recordings/${recording.slug}`} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-amber-600 no-underline">
                      <Plus className="w-3.5 h-3.5" />Add link
                    </Link>
                  </div>
                )}
              </div>
            )}

            {activeTab === "photos" && (
              <PhotosTab
                images={recording.images}
                adminControls={permissions.can_edit ? {
                  setPrimaryBasePath: "/admin/image_attachments",
                  removeBasePath:     "/admin/image_attachments",
                  deleteBasePath:     "/admin/image_attachments",
                  addImageHref:       `/admin/images/new?attachable_type=Recording&attachable_id=${recording.id}&return_to=/recordings/${recording.slug}`,
                  attachExistingHref: `/admin/image_attachments/new?attachable_type=Recording&attachable_id=${recording.id}&return_to=/recordings/${recording.slug}`,
                  returnTo:           `/recordings/${recording.slug}`,
                } : undefined}
              />
            )}
          </>
        )}

        {permissions.can_edit && (
          <ResearchAssistantPanel entityType="Recording" entityId={recording.id} />
        )}
      </PublicShell>
    </>
  )
}
